/* Re-Tech: enquiry emails, readable (30 Oct 2026).
 * Every storefront contact form is rewritten at submit time (formdata event) into four fields the team reads at a glance:
 *   Заявка  — one line: what it is, the item, the price
 *   Name / Телефон / Имейл
 *   body    — grouped sections (what, offer/parts, client details, source)
 * Labels inside the body keep the old wording ("Модел:", "Оферта от сайта:", "Телефон:" …), so anything that parses the
 * email text still finds them. Source capture is consent-aware (analytics + marketing) and is evidence only, not ad attribution.
 * Forms can hand over their own summary with form.rtSummary = () => ({headline, sections: [[title, [lines]]]}).
 * Sell form: a post without the page's body text or phone (bot, or the page's checks bypassed — 30 Oct "Kamikaze" email)
 * goes out without the email field, so Shopify rejects it instead of mailing an empty enquiry. */
(function () {
  'use strict';
  if (window.rtEnquiryAttributionInstalled) return;
  window.rtEnquiryAttributionInstalled = true;
  const key = 'retech.attribution.v1';
  const ttl = 30 * 24 * 60 * 60 * 1000;
  const fields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const refs = new WeakMap();
  const blockedSell = new WeakSet();

  /* ---------- source capture ---------- */
  function allowed() {
    const p = window.Shopify?.customerPrivacy;
    return p?.analyticsProcessingAllowed?.() === true && p?.marketingAllowed?.() === true;
  }
  function clean(value) {
    return String(value || '').slice(0, 160).replace(/[^\p{L}\p{N} _.,:|+\-/]/gu, '').trim();
  }
  function read() {
    try {
      const s = JSON.parse(localStorage.getItem(key) || 'null');
      if (s && s.version === 1 && Number.isFinite(s.at) && s.at <= Date.now() && Date.now() - s.at < ttl) return s;
    } catch (_) {}
    return null;
  }
  function remove() { try { localStorage.removeItem(key); } catch (_) {} }
  function capture() {
    if (!allowed()) { remove(); return null; }
    const old = read();
    const params = new URLSearchParams(location.search);
    const touch = {at: new Date().toISOString()};
    for (const field of fields) if (params.has(field)) touch[field] = clean(params.get(field));
    if (!touch.utm_source) {
      let host = '';
      try { host = new URL(document.referrer).hostname; } catch (_) {}
      if (host && host !== location.hostname && !host.endsWith('.myshopify.com') && host !== 'admin.shopify.com') {
        touch.utm_source = host;
        touch.utm_medium = 'referral';
      } else if (old) return old;
      else { touch.utm_source = 'direct'; touch.utm_medium = 'none'; }
    }
    const state = {version: 1, at: Date.now(), first: old?.first || touch, last: touch};
    try { localStorage.setItem(key, JSON.stringify(state)); } catch (_) {}
    return state;
  }
  function when(iso) {
    try {
      return new Date(iso).toLocaleString('bg-BG', {timeZone: 'Europe/Sofia', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'}).replace(' г.', '').replace(',', '');
    } catch (_) { return ''; }
  }
  function describe(t) {
    if (!t) return '';
    const src = String(t.utm_source || '').toLowerCase(), med = String(t.utm_medium || '').toLowerCase();
    const paid = /paid|cpc|ppc|ads?$|social_paid/.test(med);
    let who;
    if (/^(meta|facebook|fb|instagram|ig)$|facebook\.com$|instagram\.com$/.test(src)) who = paid ? 'Meta реклама (Facebook/Instagram)' : 'Facebook/Instagram';
    else if (/^google$|google\./.test(src)) who = paid ? 'Google реклама' : 'Google търсене';
    else if (src === 'direct') who = 'Директно (въведен адрес или отметка)';
    else if (med === 'referral') who = 'Линк от ' + src;
    else who = src + (med ? ' / ' + med : '');
    const bits = [who];
    if (t.utm_campaign) bits.push('кампания ' + t.utm_campaign);
    if (t.utm_content) bits.push('реклама „' + t.utm_content + '“');
    if (t.utm_term && /^\d{6,}$/.test(t.utm_term)) bits.push('ID ' + t.utm_term);
    else if (t.utm_term) bits.push('ключова дума „' + t.utm_term + '“');
    const at = t.at ? when(t.at) : '';
    return bits.join(' · ') + (at ? ' · ' + at : '');
  }

  /* ---------- email layout ---------- */
  const SKIP = new Set(['name', 'email', 'body', 'Phone', 'Телефон', 'Location', 'Град', 'Адрес', 'Продай', 'Конфигуратор', 'Форма', 'configuration',
    'Enquiry reference', 'Attribution status', 'First touch', 'Last touch']);
  const keyOf = n => { const m = /^contact\[(.*)\]$/.exec(n); return m ? m[1] : null; };
  const line = (label, v) => v ? label + ': ' + v : '';

  function kindOf(form, c) {
    if (c['Продай'] || form.id === 'rs2-form') return 'sell';
    if (c['Конфигуратор'] || form.closest?.('.rc2')) return 'pc';
    if (/^Услуги/.test(c['Форма'] || '') || form.closest?.('.sv2')) return 'service';
    return 'other';
  }

  function build(form, data) {
    const c = {}, extra = [];
    for (const [n, v] of data.entries()) {
      const k = keyOf(n); if (k == null || typeof v !== 'string') continue;
      const val = v.trim();
      if (k in c) { if (val) c[k] = c[k] ? c[k] + ', ' + val : val; } else c[k] = val;
    }
    const kind = kindOf(form, c);
    const phone = c['Телефон'] || c['Phone'] || '';
    const city = c['Град'] || c['Location'] || '';
    let headline = '', sections = [];

    let own = null;
    try { own = typeof form.rtSummary === 'function' ? form.rtSummary() : null; } catch (_) {}

    if (own) {
      headline = own.headline; sections = own.sections || [];
    } else if (kind === 'pc') {
      const body = c.body || '';
      const lines = body.split('\n');
      const isSave = /запаз|save/i.test(c['Тип'] || c['Type'] || '');
      const base = (lines[1] || '').replace(/^[^:]*:\s*/, '') || 'Собствена конфигурация';
      headline = (isSave ? 'ЗАПАЗЕНА КОНФИГУРАЦИЯ' : 'PC ЗАПИТВАНЕ') + ' · ' + base + (c['Ориентировъчна цена'] ? ' · ' + c['Ориентировъчна цена'] : '');
      sections.push(['КОНФИГУРАЦИЯ', [body]]);
    } else if (kind === 'service') {
      const picked = Object.keys(c).filter(k => k.startsWith('✓') && c[k]).map(k => k.replace(/^✓\s*/, '') + ' — ' + c[k]);
      headline = 'УСЛУГА · ' + (picked.map(p => p.split(' — ')[0]).join(', ') || 'без избрана услуга') + (c['Ориентировъчна сума'] ? ' · ~' + c['Ориентировъчна сума'] : '');
      sections.push(['ИЗБРАНИ УСЛУГИ', picked.length ? picked : ['—']]);
      sections.push(['ЛОГИСТИКА', [line('Ориентировъчна сума', c['Ориентировъчна сума']), line('Куриер за сметка на', c['Куриер за сметка на']),
        line('Изпраща', c['Изпраща']), line('Куриер', c['Куриер']), line('Град', city), line('Модел', c['Модел']), line('Фирма', c['Фирма'])]]);
      if (c.body) sections.push(['ПРОБЛЕМ / ОПИСАНИЕ', [c.body]]);
      Object.keys(c).filter(k => k.startsWith('✓')).forEach(k => SKIP.add(k));
      ['Ориентировъчна сума', 'Куриер за сметка на', 'Изпраща', 'Куриер', 'Модел', 'Фирма'].forEach(k => SKIP.add(k));
    } else if (kind === 'sell' && c.body && phone) {
      // The sell page writes "Label: value" lines (BG or EN); regroup them.
      const v = {}, groups = {what: [], offer: [], notes: [], other: []};
      const where = [
        [/^(Тип|Type|Модел|Model|Описание|Description|Състояние|Condition|Производител \/ серия|Brand \/ series|Гаранция \/ покупка|Warranty \/ purchase|Кутия \/ фактура|Box \/ invoice|Снимки|Photos|Желана цена|Asking price)$/, 'what'],
        [/^(Оферта от сайта|Website offer|Кредит при замяна|Trade-in credit|Желан продукт \(замяна\)|Wants instead)$/, 'offer'],
        [/^(Бележки|Notes)$/, 'notes'],
        [/^(Град|City)$/, null]
      ];
      for (const raw of c.body.split('\n')) {
        const ln = raw.trim(); const m = /^([^:]{2,40}):\s*(.*)$/.exec(ln);
        if (!m) continue;
        v[m[1]] = m[2];
        const hit = where.find(([re]) => re.test(m[1]));
        if (hit && hit[1] === null) continue;
        if (hit && hit[1] === 'notes' && (m[2] === '-' || !m[2])) continue;
        (hit ? groups[hit[1]] : groups.other).push(ln);
      }
      const what = v['Модел'] || v['Model'] || v['Описание'] || v['Description'] || '-';
      const cond = v['Състояние'] || v['Condition'] || '';
      const off = (v['Оферта от сайта'] || v['Website offer'] || '').replace(/\s*\(.*$/, '');
      const swap = v['Желан продукт (замяна)'] || v['Wants instead'] || '';
      headline = ['ПРОДАЖБА', what, cond, off ? (/^\d/.test(off) ? 'оферта ' + off : off) : '', swap ? 'замяна за ' + swap.split(' — ')[0] : ''].filter(Boolean).join(' · ');
      sections.push(['КАКВО ПРОДАВА', groups.what], ['ОФЕРТА', groups.offer.concat(off && /^\d/.test(off) ? ['Окончателна цена — след тест при нас.'] : [])], ['БЕЛЕЖКИ ОТ КЛИЕНТА', groups.notes.map(x => x.replace(/^[^:]*:\s*/, ''))], ['ДРУГИ ДАННИ', groups.other]);
    } else if (kind === 'sell') {
      // Sent without the page's own validation (body/phone missing): bot or a broken page. Shopify rejects it (no email).
      blockedSell.add(form);
      headline = 'ПРОДАЖБА · ⚠ без данни за картата';
      sections.push(['ВНИМАНИЕ', ['Заявката дойде без попълнена форма (модел, състояние, телефон).']]);
    } else {
      headline = 'СЪОБЩЕНИЕ ОТ САЙТА' + (c['Тип'] ? ' · ' + c['Тип'] : '');
      if (c.body) sections.push(['СЪОБЩЕНИЕ', [c.body]]);
    }

    // Anything else the form posted that is not already laid out.
    const known = own?.consumed || [];
    for (const [k, v] of Object.entries(c)) {
      if (!v || SKIP.has(k) || known.includes(k)) continue;
      if (kind === 'pc' && (k === 'Ориентировъчна цена' || ((k === 'Бележки') && (c.body || '').includes(v)))) continue;
      if (kind === 'pc' && (k === 'Тип' || k === 'Type') && /запитване|enquiry|quote/i.test(v)) continue;
      extra.push(k + ': ' + v);
    }
    if (extra.length) sections.push(['ДРУГИ ДАННИ', extra]);

    sections.push(['КЛИЕНТ', [line('Име', c.name), line('Телефон', phone || '— не е попълнен'), line('Имейл', c.email), line('Град', city), line('Адрес', c['Адрес'])]]);

    let ref = refs.get(form);
    if (!ref) { ref = 'RT-' + (window.crypto?.randomUUID ? window.crypto.randomUUID().slice(0, 8).toUpperCase() : Date.now().toString(36).toUpperCase()); refs.set(form, ref); }
    const state = capture();
    const src = [];
    if (state) {
      src.push(describe(state.last));
      if (fields.some(f => (state.first?.[f] || '') !== (state.last?.[f] || ''))) src.push('Първо посещение: ' + describe(state.first));
      src.push('(по данни от браузъра — ориентир, не доказателство от рекламата)');
    } else src.push('Неизвестен (клиентът не е приел бисквитките)');
    src.push('Номер на заявката: ' + ref);
    sections.push(['ОТКЪДЕ ДОЙДЕ', src]);

    const text = sections.map(([title, ls]) => {
      const body = ls.filter(Boolean).join('\n');
      return body ? title + '\n' + body : '';
    }).filter(Boolean).join('\n\n');
    return {headline, phone, text, name: c.name || '', email: c.email || ''};
  }

  function rewrite(form, data) {
    if (form?.tagName !== 'FORM' || !form.querySelector('[name="form_type"][value="contact"]')) return;
    // Only real submissions: a submit event just fired, or the form's own code validated it, or it is a guarded form.
    const recent = Date.now() - (submits.get(form) || 0) < 120000;
    // The sell page submits through form.submit() after its own checks (no submit event), so it is always handled.
    if (!recent && !(form.id === 'rs2-form' || form.querySelector('[name="contact[Продай]"]'))) return;
    let out;
    try { out = build(form, data); } catch (_) { return; }
    for (const n of [...new Set([...data.keys()])]) if (keyOf(n) != null) data.delete(n);
    data.append('contact[Заявка]', out.headline);
    data.append('contact[name]', out.name);
    if (out.phone) data.append('contact[Телефон]', out.phone);
    const blocked = blockedSell.has(form);
    blockedSell.delete(form);
    if (!blocked) data.append('contact[email]', out.email);
    data.append('contact[body]', out.text);
  }

  const submits = new WeakMap();
  document.addEventListener('submit', event => { if (event.target?.tagName === 'FORM') submits.set(event.target, Date.now()); }, true);
  document.addEventListener('formdata', event => rewrite(event.target, event.formData), true);
  document.addEventListener('visitorConsentCollected', capture);
  if (window.Shopify?.customerPrivacy) capture();
  else if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], error => { if (!error) capture(); });
  window.rtEnquiryEmail = {build}; // for tests
})();

/* Re-Tech: the €10 deposit is paid on its own, by card (30 Oct 2026).
 * A deposit-only checkout needs no shipping, so Shopify offers card / Shop Pay / Google Pay only. When other products sit in
 * the same cart, the order needs shipping and cash on delivery appears (order #1021, 29 Oct). So before any checkout that
 * contains the deposit, the other lines are parked in localStorage and removed; they are put back in the cart the next time
 * the visitor opens the shop with no deposit in the cart (i.e. after paying, or after removing the deposit).
 * The deposit buttons (configurator, build pages, AI page, Halloween, sell page) add the deposit with fetch('cart/add.js') and
 * then go to /checkout; the fetch wrapper below parks the other lines before that add resolves, so those files stay unchanged.
 *   window.rtDepositOnly() → Promise<true | 'held'> */
(() => {
  if (window.rtDepositOnly) return;
  const KEY = 'retech.held-cart.v1';
  const MAX_AGE = 14 * 864e5;
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const en = (document.documentElement.lang || '').slice(0, 2) === 'en';
  const T = en ? {
    title: 'The deposit is paid separately, by card',
    body: n => `The €10 deposit is paid on its own, by card, Apple Pay or Google Pay (no cash on delivery). The other ${n === 1 ? 'product' : n + ' products'} in your cart stay saved and will be back in your cart after the payment.`,
    go: 'Pay the deposit by card', cancel: 'Cancel'
  } : {
    title: 'Депозитът се плаща отделно, с карта',
    body: n => `Депозитът от 10 € се плаща самостоятелно — с карта, Apple Pay или Google Pay (без наложен платеж). ${n === 1 ? 'Другият продукт' : 'Другите ' + n + ' продукта'} в количката остава${n === 1 ? '' : 'т'} запазен${n === 1 ? '' : 'и'} и ще ${n === 1 ? 'го' : 'ги'} видиш отново в количката след плащането.`,
    go: 'Плащам депозита с карта', cancel: 'Отказ'
  };
  const isDeposit = it => it && (it.sku === 'SRV-DEPOSIT' || it.handle === 'depozit-pc-konfiguracia' || /^depozit/.test(it.handle || ''));
  const getCart = () => fetch(root() + 'cart.js', {headers: {Accept: 'application/json'}, cache: 'no-store'}).then(r => r.json());
  const post = (path, body) => fetch(root() + path, {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)});

  function ask(n) {
    return new Promise(resolve => {
      const wrap = document.createElement('div');
      wrap.setAttribute('role', 'dialog'); wrap.setAttribute('aria-modal', 'true'); wrap.setAttribute('aria-labelledby', 'rt-dep-t');
      wrap.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(0,0,0,.62);font-family:Manrope,system-ui,sans-serif';
      wrap.innerHTML = `<div style="max-width:440px;width:100%;background:#0e1311;color:#eef3f0;border:1px solid rgba(52,232,138,.35);border-radius:16px;padding:22px 20px;box-shadow:0 20px 60px rgba(0,0,0,.5)">
        <h2 id="rt-dep-t" style="margin:0 0 10px;font-size:19px;line-height:1.3;font-weight:800">${T.title}</h2>
        <p style="margin:0 0 18px;font-size:15px;line-height:1.55;color:#c4cec9">${T.body(n)}</p>
        <div style="display:flex;flex-wrap:wrap;gap:10px">
          <button type="button" data-go style="flex:1 1 220px;min-height:48px;border:0;border-radius:999px;background:linear-gradient(135deg,#5bffb0,#16b86a);color:#04130b;font-family:inherit;font-weight:800;font-size:15px;line-height:1.2;padding:0 18px;cursor:pointer">${T.go}</button>
        </div></div>`;
      const done = v => { wrap.remove(); resolve(v); };
      wrap.querySelector('[data-go]').onclick = () => done(true);
      document.body.append(wrap);
      wrap.querySelector('[data-go]').focus();
    });
  }

  async function depositOnly() {
    const cart = await getCart();
    const items = cart.items || [];
    const dep = items.filter(isDeposit), other = items.filter(it => !isDeposit(it));
    if (!dep.length || !other.length) return true;
    const ok = await ask(other.reduce((s, it) => s + it.quantity, 0));
    if (!ok) {
      for (const it of dep) await post('cart/change.js', {id: it.key, quantity: 0});
      return false;
    }
    let held = [];
    try { const prev = JSON.parse(localStorage.getItem(KEY) || 'null'); if (prev && Date.now() - prev.at < MAX_AGE) held = prev.items || []; } catch {}
    held = held.concat(other.map(it => ({id: it.variant_id, quantity: it.quantity, properties: it.properties || {}})));
    try { localStorage.setItem(KEY, JSON.stringify({at: Date.now(), items: held})); } catch {}
    const updates = {}; other.forEach(it => { updates[it.key] = 0; });
    const r = await post('cart/update.js', {updates});
    if (!r.ok) throw new Error('hold ' + r.status);
    return 'held';
  }
  window.rtDepositOnly = depositOnly;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async function (input, init) {
    const res = await nativeFetch(input, init);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const body = init && typeof init.body === 'string' ? init.body : '';
      if (res.ok && /\/cart\/add(\.js)?(\?|$)/.test(url) && body) {
        const cart = await getCart();
        const dep = (cart.items || []).find(isDeposit);
        if (dep && body.includes(String(dep.variant_id))) await depositOnly();
      }
    } catch (_) {}
    return res;
  };

  /* Put parked products back once the cart has no deposit in it. */
  async function restore() {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch {}
    if (!saved || !Array.isArray(saved.items) || !saved.items.length) return;
    if (Date.now() - saved.at > MAX_AGE) { try { localStorage.removeItem(KEY); } catch {} return; }
    const cart = await getCart();
    if ((cart.items || []).some(isDeposit)) return;
    try { localStorage.removeItem(KEY); } catch {}
    const present = new Set((cart.items || []).map(it => it.variant_id));
    const items = saved.items.filter(it => !present.has(it.id));
    if (!items.length) return;
    const r = await post('cart/add.js', {items});
    if (r.ok) { try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch {} if (/\/cart\/?$/.test(location.pathname)) location.reload(); }
  }

  /* Cart page / mini-cart checkout buttons: same rule. */
  let bypass = false;
  document.addEventListener('click', async e => {
    if (bypass) return;
    const el = e.target.closest && e.target.closest('[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]');
    if (!el) return;
    e.preventDefault(); e.stopPropagation();
    let ok = true;
    try { ok = await depositOnly(); } catch {}
    if (!ok) { location.reload(); return; }
    bypass = true;
    // After parking lines the cart form is stale (its updates[] no longer match the cart): go straight to checkout.
    if (ok === 'held') location.href = root() + 'checkout';
    else if (el.tagName === 'A') location.href = el.href;
    else if (el.form) { if (el.form.requestSubmit) el.form.requestSubmit(el); else location.href = root() + 'checkout'; }
    else location.href = root() + 'checkout';
    setTimeout(() => { bypass = false; }, 3000);
  }, true);

  const start = () => { restore().catch(() => {}); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true}); else start();
})();
